import { inject, Injectable } from "@angular/core";
import { ICustomDocumentTypesMap, IEntityDocument, IEntityDocumentVersion, UUID } from "../../core/interfaces";
import { deleteObject, getBytes, getDownloadURL, listAll, ref, Storage, uploadBytes } from "@angular/fire/storage";
import {
  arrayRemove,
  CollectionReference,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  query,
  where
} from "firebase/firestore";
import { collection, deleteDoc, Firestore, setDoc, updateDoc } from "@angular/fire/firestore";
import { chunkArray }  from "@cloud-functions/utils";
import { convertTimestampsToDates } from "../../core/utils/helper-functions";

@Injectable({
  providedIn: 'root'
})
export class DocumentManagementService {
  protected firestore: Firestore = inject(Firestore);
  protected storage: Storage = inject(Storage);

  private getDocumentsCollectionRef(): CollectionReference<IEntityDocument> {
    return collection(
      this.firestore,
      "documents"
    ) as CollectionReference<IEntityDocument>;
  }

  private getDocumentRef(docId: UUID): DocumentReference<IEntityDocument> {
    if (!docId) {
      throw new Error('Invalid document ID');
    }

    return doc(
      this.firestore,
      `documents/${docId}`
    ) as DocumentReference<IEntityDocument>;
  }

  public async getDocument(docId: UUID): Promise<IEntityDocument> {
    try {
      const documentRef = this.getDocumentRef(docId);
      const documentSnap = await getDoc(documentRef);

      if (!documentSnap.exists()) {
        throw new Error(`Document with ID ${docId} not found`);
      }

      const data = documentSnap.data();

      if (!data) {
        throw new Error(`Document data for ID ${docId} is empty or malformed`);
      }

      return convertTimestampsToDates(data);
    } catch (error) {
      console.error('Failed to get document:', error);
      throw error;
    }
  }

  public getCustomDocumentTypesRef(orgId: UUID): DocumentReference<ICustomDocumentTypesMap> {
    return doc(
      this.firestore,
      `organization/${orgId}/settings/customDocumentTypes`
    ) as DocumentReference<ICustomDocumentTypesMap>;
  }

  public async getCustomDocumentTypes(orgId: UUID): Promise<ICustomDocumentTypesMap> {
    try {
      const customDocumentTypesRef = this.getCustomDocumentTypesRef(orgId);
      const customDocumentTypesSnap = await getDoc(customDocumentTypesRef);

      if (!customDocumentTypesSnap.exists()) {
        return {}
      }

      const customDocumentTypes = customDocumentTypesSnap.data();

      console.log("getCustomDocumentTypesRef: ", customDocumentTypes);

      return customDocumentTypes || {};
    } catch (error) {
      console.error('Failed to get custom document types:', error);
      throw error;
    }
  }

  public async getDocuments(docIds: UUID[]): Promise<IEntityDocument[]> {
    try {
      const documentsCollectionRef = this.getDocumentsCollectionRef();
      const docIdChunks = chunkArray(docIds, 10);

      const documentsSnap = await Promise.all(
        docIdChunks.map(chunk =>
          getDocs(
            query(
              documentsCollectionRef,
              where("id", "in", chunk)
            )
          )
        )
      );

      return documentsSnap.flatMap(snapshot =>
        snapshot.docs.map(doc => convertTimestampsToDates(doc.data()))
      );
    } catch (error) {
      console.error('Failed to get documents:', error);
      throw error;
    }
  }

  public async createDocument(document: IEntityDocument): Promise<IEntityDocument> {
    if (!document || !document.id) {
      throw new Error('Invalid document payload');
    }

    try {
      const documentRef = this.getDocumentRef(document.id);
      await setDoc(documentRef, document);

      return this.getDocument(document.id);
    } catch (error) {
      console.error('Failed to create document:', error);
      throw error;
    }
  }

  public async updateDocument(document: IEntityDocument): Promise<IEntityDocument> {
    if (!document || !document.id) {
      throw new Error('Invalid document payload');
    }

    try {
      const documentRef = this.getDocumentRef(document.id);
      await updateDoc(documentRef, document);

      return this.getDocument(document.id);
    } catch (error) {
      console.error('Failed to update document:', error);
      throw error;
    }
  }

  public async deleteDocumentFromEntity(documentId: UUID, entityId: UUID): Promise<void> {
    const documentRef = this.getDocumentRef(documentId);
    const document = await this.getDocument(documentId);

    if (document.relatesToEntities.length === 1 && document.relatesToEntities.includes(entityId)) {
      await this.deleteDocument(documentId);
    } else {
      await updateDoc(documentRef, {
        relatesToEntities: arrayRemove(entityId)
      })
    }
  }

  public async deleteDocument(documentId: UUID): Promise<UUID> {
    if (!documentId) {
      throw new Error('Invalid document UUID');
    }

    try {
      const documentRef = this.getDocumentRef(documentId);
      await Promise.all([
        deleteDoc(documentRef),
        this.deleteAllFileVersions(documentId),
      ]);

      return documentId;
    } catch (error) {
      console.error('Failed to delete document:', error);
      throw error;
    }
  }

  public async uploadFileVersion(docId: UUID, file: File): Promise<IEntityDocumentVersion> {
    const folderRef = ref(this.storage, `doc_management/${docId}`);
    const listResult = await listAll(folderRef);
    const nextVersion = listResult.items.length + 1;

    const filePath = `doc_management/${docId}/version_${nextVersion}`;
    const storageRef = ref(this.storage, filePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      fileRef: downloadURL,
      uploadedDate: new Date(),
      originalFileName: file.name,
    };
  }

  public async deleteFileVersion(docId: UUID, versionToRemove: number): Promise<void> {
    const folderRef = ref(this.storage, `doc_management/${docId}`);
    const listResult = await listAll(folderRef);

    // Delete the target version
    const targetRef = ref(this.storage, `doc_management/${docId}/version_${versionToRemove}`);
    await deleteObject(targetRef);

    // Filter files with a version number greater than the one removed
    const versionedFiles = listResult.items
      .map(item => ({
        ref: item,
        version: this.extractVersionNumber(item.name)
      }))
      .filter(item => item.version > versionToRemove)
      .sort((a, b) => a.version - b.version);

    // Rename subsequent versions to close the gap (e.g., version_3 -> version_2)
    for (const item of versionedFiles) {
      const newVersion = item.version - 1;
      const newPath = `doc_management/${docId}/version_${newVersion}`;

      const fileData = await getBytes(item.ref);   // Read the original file
      const newRef = ref(this.storage, newPath);
      await uploadBytes(newRef, fileData);         // Upload with new version name
      await deleteObject(item.ref);                // Delete the old file
    }
  }

  // Helper method to extract version number from file name
  private extractVersionNumber(fileName: string): number {
    const match = fileName.match(/version_(\d+)/);
    return match ? parseInt(match[1], 10) : -1;
  }

  public async deleteAllFileVersions(docId: UUID): Promise<void> {
    const folderRef = ref(this.storage, `doc_management/${docId}`);
    const listResult = await listAll(folderRef);

    const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);
  }

  public async setCustomDocumentTypes(
    orgId: UUID,
    customDocumentTypes: ICustomDocumentTypesMap
  ): Promise<ICustomDocumentTypesMap> {
    const customDocumentTypesRef = this.getCustomDocumentTypesRef(orgId);
    await setDoc(customDocumentTypesRef, customDocumentTypes);

    return this.getCustomDocumentTypes(orgId);
  }
}
